/* eslint-disable max-len */
import { nestCall, NestApiType } from '@/composables/nestApi';
import { SpmTableState } from '@/types';

interface ListResponse<T> {
  items: Array<T>;
  total: number;
  err: string;
}

const formatDateModification = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export default async function getTemplatesUsedBySyncElement({ idShop, syncElementIds }: { idShop: number; syncElementIds: string[] }): Promise<SpmTableState> {
  const response = await nestCall(
    'post',
    NestApiType.DEFAULT,
    '/template/sync/used-by-elements',
    {},
    '',
    {
      id_shop: idShop,
      sync_element_ids: syncElementIds,
    },
  );

  if (response?.error) {
    return {
      error: response.error?.message || response.error || 'Request error',
      items: [],
      total: 0,
    };
  }

  const result: ListResponse<any> = response ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items.map((item) => ({
      ...item,
      label: item.name_campaign_automation ? `${item.label} (${item.name_campaign_automation})` : item.label,
      date_modification: formatDateModification(item.date_modification),
    })),
    total: result.total,
  };
}

export async function getTemplatesNotUsedBySyncElement({
  idShop,
  syncElementId,
  templateType,
  elementPartTypeOrSubtype,
  limit = 10,
  lastTemplateId,
  isPartType,
  excludeAutomationTemplates = false,
  excludeIds = [],
  version,
}: {
  idShop: number;
  syncElementId: string;
  templateType: string;
  elementPartTypeOrSubtype: string;
  limit?: number;
  lastTemplateId?: number;
  isPartType?: boolean;
  excludeAutomationTemplates?: boolean;
  excludeIds?: number[];
  version?: number;
}): Promise<SpmTableState> {
  const response = await nestCall(
    'post',
    NestApiType.DEFAULT,
    '/template/sync/not-used',
    {},
    '',
    {
      id_shop: idShop,
      sync_element_id: syncElementId,
      template_type: templateType,
      element_part_type_or_subtype: elementPartTypeOrSubtype,
      limit,
      last_template_id: lastTemplateId,
      is_part_type: isPartType,
      exclude_automation_templates: excludeAutomationTemplates,
      exclude_ids: excludeIds,
      version,
    },
  );

  if (response?.error) {
    return {
      error: response.error?.message || response.error || 'Request error',
      items: [],
      total: 0,
    };
  }

  const result: ListResponse<any> = response ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items.map((item) => ({
      ...item,
      date_modification: formatDateModification(item.date_modification),
    })),
    total: result.total,
  };
}

export async function getCampaignAutomationsWithTemplatesNotUsedBySyncElement({
  idShop,
  syncElementId,
  templateType,
  elementPartTypeOrSubtype,
  limit = 10,
  lastWorkflowId,
  lastCreationDate,
  isPartType,
  excludeIds = [],
  version,
}: {
  idShop: number;
  syncElementId: string;
  templateType: string;
  elementPartTypeOrSubtype: string;
  limit?: number;
  lastWorkflowId?: number;
  lastCreationDate?: string;
  isPartType?: boolean;
  excludeIds?: number[];
  version?: number;
}): Promise<SpmTableState> {
  const response = await nestCall(
    'post',
    NestApiType.DEFAULT,
    '/template/sync/automations-not-used',
    {},
    '',
    {
      id_shop: idShop,
      sync_element_id: syncElementId,
      template_type: templateType,
      element_part_type_or_subtype: elementPartTypeOrSubtype,
      limit,
      last_workflow_id: lastWorkflowId,
      last_creation_date: lastCreationDate,
      is_part_type: isPartType,
      exclude_ids: excludeIds,
      version,
    },
  );

  if (response?.error) {
    return {
      error: response.error?.message || response.error || 'Request error',
      items: [],
      total: 0,
    };
  }

  const result: ListResponse<any> = response ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items.map((item) => ({
      ...item,
      date_modification: formatDateModification(item.date_modification),
      templates: Array.isArray(item.templates)
        ? item.templates.map((template: any) => ({
          ...template,
          date_modification: formatDateModification(template.date_modification),
        }))
        : [],
    })),
    total: result.total,
  };
}

